"use client";

// React imports
import { useEffect } from "react";

// Component & Template imports
import NextLink from "@/components/interactive/NextLink";

// Hooks, Utility & Helper function imports
import scrollToAnchor from "@/utils/scrollToAnchor";
import { IsovContent, IsovButton } from "@/components/stencil";
import NextImage from "@/components/interactive/NextImage";
import { useLocale } from "@/components/providers/LocaleProvider";
import { getStaticDictionary } from "@/dictionaries/client-sync";

const BASE_URL = `${process.env.NEXT_PUBLIC_BASE_URL}`;

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const skeleton = false;
  const locale = useLocale();
  const dict = getStaticDictionary(locale);
  const ui = dict.ui.error;

  const links = [{ linkLabel: ui.goHome, linkUrl: "/" }];

  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToAnchor({ id: "#top", behavior: "smooth" });
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    console.error(error);

    fetch("/api/monitoring/client-error/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: error.message,
        digest: error.digest,
        stack: error.stack,
        pathname: window.location.pathname,
      }),
      keepalive: true,
    }).catch(() => {});
  }, [error]);

  return (
    <main id="top">
      <section id="not-found" className="narrow unpadded">
        <NextImage
          src={`${BASE_URL}images/500.svg`}
          alt="500 - Server error!"
          className="w-full max-w-96 mt-12 mx-auto"
          maxWidth={800}
          height={486}
          width={269}
          priority
        />

        <IsovContent className="my-0 text-center">
          <h1 className="mb-6">{ui.title}</h1>

          <p>{ui.description}</p>

          <div className="flex flex-wrap items-center content-center justify-center justify-items-center justify-content-center text-center gap-3 sm:gap-6 w-full max-w-max mx-auto">
            <NextLink
              href="#"
              className="inline-block mx-auto"
              onClick={() => reset()}
              type="button_tertiary"
              slot="cta"
            >
              <IsovButton
                variant="tertiary"
                label={ui.tryAgain}
                skeleton={skeleton}
              />
            </NextLink>

            {links?.map(({ linkLabel, linkUrl }: any) => (
              <NextLink
                key={linkLabel + linkUrl}
                href={linkUrl}
                className="inline-block mx-auto"
                type="button_tertiary"
                slot="cta"
              >
                <IsovButton
                  variant="tertiary"
                  label={linkLabel}
                  skeleton={skeleton}
                />
              </NextLink>
            ))}
          </div>
        </IsovContent>
      </section>
    </main>
  );
}
